import { faCircleQuestion, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { Loader } from 'src/components/Loader';
import { InputAdornment, TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { externalLinks } from 'src/externalLinks';
import { fetchFaqCategories } from 'src/models/contentFaq/contentFaq';

import { FaqCenteredState, FaqWebviewShell } from './FaqWebviewShell';

const rowLinkStyle = {
  color: 'inherit',
  display: 'block',
  textDecoration: 'none',
};

export default function FaqIndexPage() {
  const { t } = useTranslation('translation', { keyPrefix: 'appWebview.faq' });
  const navigate = useNavigate();

  const {
    data: categories = [],
    error,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['faq-categories'],
    queryFn: fetchFaqCategories,
  });

  if (isLoading) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Loader
            label={t('loadingFaqs', { defaultValue: 'Loading FAQs...' })}
            size="medium"
            sx={{
              minWidth: 'auto',
              width: 'auto',
            }}
          />
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  if (isError) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Typography color="error">
            {error instanceof Error
              ? error.message
              : t('topicsLoadError', {
                  defaultValue: 'Unable to load FAQ topics.',
                })}
          </Typography>
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  return (
    <FaqWebviewShell>
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', p: 2 }}>
        <TextField
          disabled="click-only"
          onClick={() => navigate('/mobile/faq/search')}
          placeholder={t('searchPlaceholder', { defaultValue: 'Search FAQs' })}
          startAdornment={
            <InputAdornment position="start">
              <Icon color="action" icon={faSearch} />
            </InputAdornment>
          }
          sx={{ bgcolor: 'background.paper' }}
        />

        <Box
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            mt: 2,
            width: '100%',
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
          <Typography component="h1" fontWeight={700} variant="h6">
            {t('topicsHeading', { defaultValue: 'FAQ Topics' })}
          </Typography>

          {categories.length ? (
            categories.map((category) => (
              <RouterLink
                key={String(category.id)}
                style={rowLinkStyle}
                to={`/mobile/faq/category/${category.id}`}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    display: 'flex',
                    gap: 1.5,
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Icon color="primary" icon={faCircleQuestion} />
                  <Typography>{category.title}</Typography>
                </Box>
              </RouterLink>
            ))
          ) : (
            <Typography color="text.secondary">
              {t('emptyTopics', { defaultValue: 'No FAQ topics available.' })}
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 'auto', pt: 3 }}>
          <Button
            fullWidth
            onClick={() => {
              window.location.href = externalLinks.contactUs();
            }}
            variant="prominent"
          >
            {t('contactUs', { defaultValue: 'Contact Us' })}
          </Button>
        </Box>
      </Box>
    </FaqWebviewShell>
  );
}
